import { getStihoRedirectUrl } from '@cutr/constants/cutlist-theme.ts';
import { useFlag } from '@unleash/proxy-client-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useIsLoggedIn } from '@/api/login';
import { useActiveGroup, useMaterialGroupState } from '@/api/materialsGroup';
import { useMixedNestingStore } from '@/api/mixedNesting';
import { usePricingStore } from '@/api/pricing';
import { useCutlistState, useSetTitle, useTitle } from '@/api/store';
import { EmptyMultiProductCutlist } from '@/blocks/EmptyMultiProductCutlist';
import { EstimatedPrice } from '@/blocks/Price';
import { RequestUnauthQuoteFlow } from '@/blocks/RequestUnauthQuoteFlow';
import { StarterAside } from '@/blocks/StarterAside';
import { StepNavigation } from '@/blocks/StepNavigation';
import { onFocusSelect, useIsNextDisabled } from '@/hooks';
import { useIframeListeners } from '@/hooks/order';
import { MaterialGroupTabNav } from '@/interfaces/MaterialGroupTabNav';
import { Nesting, NestingHooks } from '@/interfaces/Nesting';
import { PartTable, SkeletonTable } from '@/interfaces/PartTable/index';
import { Options } from '@/interfaces/PartTableCommon/Components';
import { Input } from '@/primitives/Input';
import { useUpdateCutlist } from '@/queries/crud';
import {
  getSourceConfig,
  isStarterTier,
  useCurrentFeatures,
  useTicker,
} from '@/theme';
import { isInIframe } from '@/utils/misc';

import { LlmFileImport } from './LlmFileImport';

export const OrderPage = () => {
  const { beamProfiling, llmFileImport } = useCurrentFeatures();
  const materialGroups = useMaterialGroupState((state) => state.groups);
  const emptyCutlist = materialGroups.every(
    (group) => !group.core1 && !group.beamMaterialId
  );
  const isLoggedIn = useIsLoggedIn();
  const isCustomerExperimentEnabled = useFlag('customer-llm-file-import');
  const isComputingNesting = useMixedNestingStore(
    (state) => state.nestingState === 'computing'
  );
  const partsDisabled = isStarterTier() && isComputingNesting;

  if (emptyCutlist && beamProfiling) {
    return <EmptyMultiProductCutlist />;
  }

  return (
    <div className="content">
      <Header />
      {llmFileImport && isCustomerExperimentEnabled && isLoggedIn && (
        <LlmFileImport />
      )}
      <SubHeader />
      <MaterialGroupTabNav />
      <section className="layout">
        <main>
          <PartTable disabled={partsDisabled} />
        </main>
        <Aside />
      </section>

      <IframeListeners />
      <p />
    </div>
  );
};

export const SubHeader = () => {
  return <Options />;
};

export const Header = () => {
  const title = useTitle();
  const setTitle = useSetTitle();
  const { t } = useTranslation();
  const shortId = useCutlistState((state) => state.shortId);
  const { integrationType } = useCurrentFeatures();

  if (integrationType === 'webshop') return null;

  return (
    <div className="opposites">
      <div>
        <label htmlFor="cutlist-title">{shortId ? shortId : ''}</label>
        <Input
          onInput={(e) => setTitle(e.currentTarget.value)}
          value={title}
          className="title"
          placeholder={t('cutlist-form.field.orderTitle.placeholder')}
          id="cutlist-title"
          onFocus={onFocusSelect}
        />
      </div>
      <StepNavigation />
    </div>
  );
};

const Aside = () => {
  if (isStarterTier()) return <StarterAside />;
  return <LegacyAside />;
};

const LegacyAside = () => {
  const source = useTicker();
  const hidePriceForAgents =
    isInIframe() && ['VERDOUW', 'BOUWPROFI'].includes(source);

  const isBeamGroup = useActiveGroup()?.type === 'beams';

  return (
    <aside>
      <NestingHooks />
      {!isBeamGroup && <Nesting />}
      {!hidePriceForAgents && <Price />}
    </aside>
  );
};

const Price = () => {
  const navigate = useNavigate();
  const source = useTicker();
  const isNextDisabled = useIsNextDisabled();
  const { manualQuote } = usePricingStore();
  const { orderId } = useCutlistState();
  const { mutateAsync } = useUpdateCutlist();
  const { backBtnUrl } = getSourceConfig();
  const { integrationType, allowUnauthUsers } = useCurrentFeatures();
  const isLoggedIn = useIsLoggedIn();

  const [isRequestUnauthQuoteOpen, setIsRequestUnauthQuoteOpen] =
    React.useState(false);

  const stihoRedirect = getStihoRedirectUrl(
    orderId!,
    import.meta.env.VITE_CUTR_ENV
  );

  return (
    <>
      <EstimatedPrice
        onAction={async () => {
          if (allowUnauthUsers && !isLoggedIn) {
            setIsRequestUnauthQuoteOpen(true);
            window.analytics.track(
              'Request quote button clicked by unauth user'
            );
            return;
          }

          if (source === 'STIHO') {
            if (manualQuote) {
              navigate(`/cutlist/${orderId}/details`);
              return;
            }

            window.location.replace(stihoRedirect);
            return;
          }

          if (integrationType === 'webshop') {
            await mutateAsync({ syncThirdParty: true });

            window.location.replace(backBtnUrl);
            return;
          }

          navigate(`/cutlist/${orderId}/details`);
          window.analytics.track('Navigated to contact details', {
            ticker: source,
          });
        }}
        isNextDisabled={isNextDisabled}
      />
      {isRequestUnauthQuoteOpen && (
        <RequestUnauthQuoteFlow
          closeRequestFlow={() => setIsRequestUnauthQuoteOpen(false)}
        />
      )}
    </>
  );
};

// isolated to minimize rerenders in parent component
const IframeListeners = () => {
  useIframeListeners();

  return null;
};

export const SkeletonOrderPage = () => {
  const { beamProfiling } = useCurrentFeatures();

  if (beamProfiling) {
    return null;
  }

  return (
    <div className="content">
      <Header />
      <SubHeader />
      <section className="layout">
        <main>
          <SkeletonTable />
        </main>
        <Aside />
      </section>
    </div>
  );
};
